"use client";

import * as React from "react";
import {
  Legend,
  Tooltip,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  ChartBlock,
  ChartScaleToggle,
  ChartUnitToggle,
  exploreChartLines,
  chartXAxis,
  chartYAxis,
} from "@/components";
import { useChartScale } from "@/hooks/use-chart-scale";
import {
  heartbeatRowsForUnit,
  useHeartbeat,
  type HeartbeatDailyRow,
} from "@/hooks/use-heartbeat";
import { formatHexCompactLabel } from "@/utils/format-hex";
import {
  exploreChartMargin,
  exploreLegendProps,
  gridConfig,
  tooltipConfig,
  prepareChartDataForScale,
} from "@/utils";

const STAKE_LINES = [
  { dataKey: "inflow", name: "Stake inflow", stroke: "#03FF82" },
  { dataKey: "outflow", name: "Stake outflow", stroke: "#FF851F" },
  { dataKey: "stakedSupplyChange", name: "Staked supply change", stroke: "#00EAFF" },
] as const;

const PENALTY_LINES = [
  { dataKey: "penalties", name: "Penalties", stroke: "#FF0F6F" },
] as const;

function HeartbeatChart({
  title,
  lines,
  rawRows,
  isLoading,
  defaultLogScale = false,
  signedOutflow = false,
}: {
  title: string;
  lines: readonly { dataKey: string; name: string; stroke: string }[];
  rawRows: HeartbeatDailyRow[];
  isLoading: boolean;
  defaultLogScale?: boolean;
  signedOutflow?: boolean;
}) {
  const [unit, setUnit] = React.useState<"hex" | "usd">("hex");
  const { logScale, setLogScale } = useChartScale(defaultLogScale);
  const [hide, setHide] = React.useState<string[]>([]);
  const lineList = [...lines];

  const chartRows = React.useMemo(
    () => heartbeatRowsForUnit(rawRows, unit, { signedOutflow }),
    [rawRows, unit, signedOutflow],
  );

  const chartScaleData = React.useMemo(
    () => prepareChartDataForScale(chartRows, lineList, logScale),
    [chartRows, lineList, logScale],
  );

  const tooltipFormat = unit === "usd" ? "usd" : "hex";

  return (
    <ChartBlock
      title={title}
      isLoading={isLoading}
      slot={
        <div className="flex flex-wrap items-center gap-2">
          <ChartUnitToggle unit={unit} onChange={setUnit} />
          <ChartScaleToggle logScale={logScale} onChange={setLogScale} />
        </div>
      }
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartScaleData.data} margin={exploreChartMargin}>
          {chartXAxis({ hideLabel: true })}
          {chartYAxis({
            label: unit === "hex" ? "HEX" : "USD",
            logScale,
            valueFormat: "compact",
            domain: chartScaleData.yDomain,
          })}
          <Legend
            {...exploreLegendProps({
              lineKeys: lineList.map((line) => line.dataKey),
              hidden: hide,
              onHiddenChange: setHide,
              xAxisLabel: "HEX day",
            })}
          />
          <Tooltip {...tooltipConfig({ valueFormat: tooltipFormat })} />
          <CartesianGrid {...gridConfig()} />
          {exploreChartLines(lineList, hide)}
        </LineChart>
      </ResponsiveContainer>
    </ChartBlock>
  );
}

export function HeartbeatExplore() {
  const { data, isLoading, error } = useHeartbeat(true, "all");

  const summary = data?.summary;
  const rawRows = data?.data ?? [];

  const metrics = [
    {
      label: "Stake inflow (30d)",
      value: summary?.inflowHex ?? 0,
      tone: "text-emerald-400",
    },
    {
      label: "Stake outflow (30d)",
      value: summary?.outflowHex ?? 0,
      tone: "text-orange-400",
    },
    {
      label: "Penalties (30d)",
      value: summary?.penaltiesHex ?? 0,
      tone: "text-fuchsia-400",
    },
  ];

  return (
    <div className="space-y-12">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-2xs font-semibold uppercase tracking-wider text-cyan-300">
          eHEX
        </span>
        <span className="text-muted">+</span>
        <span className="rounded-md border border-fuchsia-400/30 bg-fuchsia-400/10 px-2.5 py-1 text-2xs font-semibold uppercase tracking-wider text-fuchsia-300">
          pHEX
        </span>
        <span className="text-sm text-muted">
          Combined · day {data?.meta.firstDay ?? "—"}–{data?.meta.lastDay ?? "—"}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-6">
        {metrics.map(({ label, value, tone }) => (
          <div key={label}>
            <p className="text-2xs font-medium uppercase tracking-wider text-muted">
              {label}
            </p>
            <p className={`mt-1 font-mono text-2xl font-semibold tracking-tight ${tone}`}>
              {isLoading ? "—" : formatHexCompactLabel(value)}
            </p>
          </div>
        ))}
      </div>

      {error && (
        <p className="text-sm text-fuchsia-400">
          Could not load heartbeat data. Try refreshing the page.
        </p>
      )}

      <HeartbeatChart
        title="Stake flows"
        lines={STAKE_LINES}
        rawRows={rawRows}
        isLoading={isLoading}
        defaultLogScale={false}
        signedOutflow
      />

      <HeartbeatChart
        title="Penalties"
        lines={PENALTY_LINES}
        rawRows={rawRows}
        isLoading={isLoading}
        defaultLogScale
      />
    </div>
  );
}
